import JSZip from 'jszip'
import ralewayBold from '../PDFrender/Raleway-Bold.ttf'
import ralewayRegular from '../PDFrender/Raleway-Regular.ttf'

/** Font family used in the DOCX, the same one the PDF uses. */
export const docxFontName = 'Raleway'

/** Raleway font files to embed in the DOCX. */
export interface IDocxFonts {
  regular: Uint8Array
  bold: Uint8Array
}

/**
 * Loads the Raleway font files that are bundled with the PDF renderer.
 *
 * @returns {Promise<IDocxFonts>} The regular and bold font files.
 */
export const loadDocxFonts = async (): Promise<IDocxFonts> => {
  const [regular, bold] = await Promise.all(
    [ralewayRegular, ralewayBold].map(async (url: string) => new Uint8Array(await (await fetch(url)).arrayBuffer()))
  )
  return { regular, bold }
}

/**
 * Obfuscates a font for embedding as described in ECMA-376 Part 2: the first 32 bytes
 * are XORed with the bytes of the font key GUID in reverse order.
 *
 * @param {Uint8Array} font - The font file.
 * @param {string} fontKey - The font key GUID without braces.
 * @returns {Uint8Array} The obfuscated font.
 */
export const obfuscateFont = (font: Uint8Array, fontKey: string): Uint8Array => {
  const keyBytes = (fontKey.replace(/-/g, '').match(/../g) ?? []).map(hex => parseInt(hex, 16)).reverse()
  const result = new Uint8Array(font)
  for (let i = 0; i < 32 && i < result.length; i++) {
    result[i] ^= keyBytes[i % keyBytes.length]
  }
  return result
}

/**
 * Finishes the font embedding of a generated DOCX:
 * - Adds the bold font file. The docx library only embeds a regular file for each font,
 *   so without this bold text is drawn by thickening the regular font.
 * - Writes the font keys in upper case like Word does. LibreOffice ignores embedded fonts
 *   whose keys are in lower case, which is how the docx library writes them.
 * - Tells Word to keep the embedded fonts when the document is edited and saved.
 *
 * @param {Blob} docx - The DOCX generated with the regular font embedded.
 * @param {Uint8Array} bold - The bold font file.
 * @returns {Promise<Blob>} The DOCX with both fonts embedded.
 */
export const completeFontEmbedding = async (docx: Blob, bold: Uint8Array): Promise<Blob> => {
  const zip = await JSZip.loadAsync(await docx.arrayBuffer())
  const fontTablePath = 'word/fontTable.xml'
  const relsPath = 'word/_rels/fontTable.xml.rels'
  const settingsPath = 'word/settings.xml'
  const [fontTable, rels, settings] = await Promise.all(
    [fontTablePath, relsPath, settingsPath].map(path => zip.file(path)?.async('string'))
  )
  const regularEmbed = new RegExp(`(<w:font w:name="${docxFontName}">[\\s\\S]*?<w:embedRegular [^>]*/>)`)
  if (!fontTable || !rels || !settings || !regularEmbed.test(fontTable)) {
    return docx
  }

  const fontKey = crypto.randomUUID().toUpperCase()
  const relationshipId = 'rIdBold'
  zip.file(`word/fonts/${docxFontName}-Bold.odttf`, obfuscateFont(bold, fontKey))
  zip.file(
    relsPath,
    rels.replace(
      '</Relationships>',
      `<Relationship Id="${relationshipId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/font" Target="fonts/${docxFontName}-Bold.odttf"/></Relationships>`
    )
  )
  zip.file(
    fontTablePath,
    fontTable
      .replace(/w:fontKey="\{([^}]+)\}"/g, (_match, key: string) => `w:fontKey="{${key.toUpperCase()}}"`)
      .replace(regularEmbed, `$1<w:embedBold r:id="${relationshipId}" w:fontKey="{${fontKey}}"/>`)
  )
  if (!settings.includes('<w:embedTrueTypeFonts')) {
    zip.file(
      settingsPath,
      settings.replace('<w:displayBackgroundShape/>', '<w:displayBackgroundShape/><w:embedTrueTypeFonts/>')
    )
  }

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })
}
